import { MediaController } from 'media-chrome/react';
import ReactPlayer from 'react-player';
import React, { useEffect, useRef, useState } from 'react';

import SimpleImage from './image-simple';
import { Button } from '$partials/ui';
import { useMediaGallery } from './media-gallery';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

const mapRange = (value, inMin, inMax, outMin, outMax) => {
    return ((value - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin;
};

/**
 * @type {import('$types/components/partials/media.d.ts').Video}
 */
const GalleryVideo = ({
    title = '',
    embed = {},
    poster = '',
    src = '',
    autoplay = false,
    loop = false,
    showControls = false,
    posterClasses = '',
    id
}) => {
    const ctx = useMediaGallery();
    const playerRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(autoplay);
    const [duration, setDuration] = useState(0);
    const [played, setPlayed] = useState(0);
    const [showPoster, setShowPoster] = useState(true);

    const handleTimeUpdate = () => {
        const player = playerRef.current;
        setPlayed(player.currentTime);
    };

    const handleDurationChange = () => {
        const player = playerRef.current;
        setDuration(player.duration);
    };

    useEffect(() => {
        if (ctx.videoId !== id) {
            setIsPlaying(false);
            setShowPoster(true);
        } else {
            setPlayed(0);
        }
    }, [ctx?.videoId]);

    return (
        <>
            <div className='absolute bottom-20 sm:bottom-auto sm:top-[36px] w-[calc(100%-148px)] sm:w-[calc(100%-200px)] left-8 sm:left-24 flex items-center z-10'>
                <Button
                    className='px-10 type-style-s text-text-default-invert'
                    onClick={() => {
                        setIsPlaying(!isPlaying);
                        setShowPoster(false);
                        if (!isPlaying) {
                            ctx.setVideoId(id);
                        }
                    }}>
                    {isPlaying ? 'Pause' : 'Play'}
                </Button>
                <input
                    className='w-full h-1 input-slider'
                    max='100'
                    min='0'
                    onChange={(e) => {
                        const player = playerRef.current;
                        const newPlayed = mapRange(parseFloat(e.target.value), 0, 100, 0, duration);
                        player.currentTime = newPlayed;

                        setPlayed(newPlayed);
                    }}
                    style={{ '--track-progress': `${100 * (mapRange(played, 0, duration, 0, 100) - 0) / (100 - 0)}` }}
                    type='range'
                    value={played ? mapRange(played, 0, duration, 0, 100) : 0}/>
            </div>
            <div className={`${embed?.url ? '' : 'h-full'} w-full`}>
                <MediaController
                    className='expand-interaction flex flex-grow'
                    style={{
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center',
                        width: '100%',
                        aspectRatio: embed.width && embed.height ? `${embed.width}/${embed.height}` : '16 / 9',
                    }}>
                    <ReactPlayer
                        crossOrigin
                        controls={false}
                        light={
                            (autoplay || !showPoster) ? false : <SimpleImage
                                className={`w-full ${posterClasses ? posterClasses : ''}`}
                                src={poster} />
                        }
                        loop={loop}
                        onClickPreview={() => {
                            ctx.setVideoId(id);
                            setIsPlaying(true);
                            setShowPoster(false);
                        }}
                        onDurationChange={handleDurationChange}
                        onEnded={() => setShowPoster(true)}
                        onPause={() => setIsPlaying(false)}
                        onPlay={() => {
                            setIsPlaying(true);
                            setShowPoster(false);
                        }}
                        onTimeUpdate={handleTimeUpdate}
                        pip={false}
                        playing={isPlaying}
                        playsInline={true}
                        ref={playerRef}
                        slot='media'
                        src={src}
                        style={{
                            height: '100%',
                            objectFit: embed?.url ? 'cover' : 'contain',
                            objectPosition: 'center',
                            width: '100%'
                        }}
                        title={title}>
                    </ReactPlayer>
                </MediaController>
            </div>
        </>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

export default GalleryVideo;
